import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors
} from '@dnd-kit/core'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import {
  SortableContext,
  rectSortingStrategy
} from '@dnd-kit/sortable'
import { useMemo, useState } from 'react'

import {
  CoverTypes,
  LiveUser,
  PageWorkflowStatus
} from 'shared/types/global'

import { PagePreview } from '../../hooks/usePagePreviews'
import { DndPage, PageTypes } from '../../type'
import { isCoverPage } from '../../utils'
import { Container, PagePair } from './Styles'
import Page from './components/Page'
import PageSkeleton from './components/Page/Skeleton'
import { PagePositions } from './components/Page/types'

type EditingLiveUsersMap = { [projectId: string]: LiveUser }

type DndPageGroup = [DndPage] | [DndPage, DndPage]

type DndPagesProps = {
  items: DndPage[]
  isLoading: boolean
  assumedPageCount: number
  editingLiveUsersMap: EditingLiveUsersMap
  onOrderChange: (from: number, to: number) => void
  onPageDragging: (previewUrl: string | null) => void
  groupId: string
  projectId: string
  coverType: CoverTypes | null
  getPreviewUrl: (pageId: string) => PagePreview
  onPageEditClick: (pageId: string) => void
  onPageCopyClick: (pageId: string) => void
  onPageViewClick: (pageId: string) => void
  onPageDeleteClick: (pageId: string) => void
  onPageStatusChange: (
    pageId: string,
    status: PageWorkflowStatus
  ) => void
}

// Group pages into pairs
const groupPages = (items: DndPage[]) => {
  return items.reduce<DndPageGroup[]>((acc, item, index) => {
    if (index % 2 === 0) {
      acc.push([item])
      return acc
    }

    acc[acc.length - 1].push(item)
    return acc
  }, [])
}

const DndPages: React.FC<DndPagesProps> = ({
  items,
  isLoading,
  assumedPageCount,
  editingLiveUsersMap,
  getPreviewUrl,
  onPageDragging,
  onOrderChange,
  onPageEditClick,
  onPageCopyClick,
  onPageViewClick,
  onPageDeleteClick,
  onPageStatusChange
}) => {
  const [isDropProhibited, setIsDropProhibited] = useState(false)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 }
    })
  )

  const skeletonPages = useMemo(() => {
    const groupedSkeletonPages = new Array(
      Math.floor(assumedPageCount / 2)
    ).fill([null, null])

    if (assumedPageCount % 2 === 1) {
      groupedSkeletonPages.push([null])
    }

    return groupedSkeletonPages
  }, [assumedPageCount])

  const groupedPages = useMemo(
    () => (isLoading ? skeletonPages : groupPages(items)),
    [items, isLoading]
  )

  return (
    <Container>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToWindowEdges]}
        onDragEnd={({ active, over }) => {
          onPageDragging(null)

          // Cover pages cannot be moved
          if (isCoverPage(over?.id as string)) {
            return
          }

          const from = active.data.current?.sortable.index
          const to = over?.data.current?.sortable.index

          if (from === undefined && to === undefined) {
            return
          }

          onOrderChange(from, to)
        }}
        onDragMove={({ active, over }) => {
          onPageDragging(getPreviewUrl(active?.id as string).url)

          const id = over?.id

          if (!id) {
            return
          }

          // Cover pages cannot be moved
          if (isCoverPage(id.toString())) {
            setIsDropProhibited(true)
            return
          }

          setIsDropProhibited(false)
        }}
      >
        <SortableContext items={items} strategy={rectSortingStrategy}>
          {groupedPages.map((group, i) => (
            <PagePair key={i}>
              {group.map((item: any, j: number) => {
                return isLoading || !item ? (
                  <PageSkeleton key={`${i}-${j}`} />
                ) : (
                  <Page
                    key={item.id}
                    id={item.id}
                    // index={i * 2 + j}
                    position={
                      j % 2 === 0
                        ? PagePositions.left
                        : PagePositions.right
                    }
                    type={item.type}
                    contentData={item.contentData}
                    preview={getPreviewUrl(item.id)}
                    isDropProhibited={isDropProhibited}
                    editingLiveUser={editingLiveUsersMap[item.id]}
                    onEdit={() => onPageEditClick(item.id)}
                    onCopy={() => onPageCopyClick(item.id)}
                    onView={() => onPageViewClick(item.id)}
                    onDelete={() => onPageDeleteClick(item.id)}
                    onStatusChange={status =>
                      onPageStatusChange(item.id, status)
                    }
                  />
                )
              })}
            </PagePair>
          ))}
        </SortableContext>
      </DndContext>
    </Container>
  )
}

export default DndPages
